import { createFileRoute, type SearchSchemaInput } from "@tanstack/react-router";
import { ClimateLoopExperiment } from "@charphq/ui/components/experiments";
// Deep import rather than the `lib/experiments` barrel: `validateSearch` runs
// before the code-split component loads, so anything it reaches lands in the
// entry chunk. The barrel would drag the dataset modules in with it.
import { parseClimateLoopSearch, type ClimateLoopSearch } from "@charphq/ui/lib/experiments/search";
import { useSeasonalClimate } from "../lib/useSeasonalClimate";

/**
 * Code-split automatically: `vite.config.ts` runs
 * `tanstackRouter({ autoCodeSplitting: true })`, so this component leaves the
 * main bundle without needing `createLazyFileRoute`.
 *
 * `SearchSchemaInput` marks the *input* as partial while the output stays
 * complete, so a link into this route does not have to spell out every param.
 *
 * Like Live Network — and unlike the four remaining stubs — this route does not
 * use `ExperimentRoutePage`: Climate Loop has no metric toggle (both axes are in
 * use), an extra isolate control, and `highlight` / `isolate` state of its own,
 * so it owns its search schema and renders the experiment directly.
 */
export const Route = createFileRoute("/experiments/climate-loop")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) =>
    parseClimateLoopSearch(search),
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // The loop reads the same per-site series the wheel does, so it shares the query and its cache
  // entry — moving between the two screens costs no second Influx scan.
  const query = useSeasonalClimate();

  /**
   * Every control writes to the URL rather than to local state, which is what
   * makes a projected screen reproducible from its address. `replace: true`
   * keeps a session of fiddling with filters from burying the previous page
   * under fifty history entries.
   *
   * Re-parsing rather than spreading guarantees a complete, valid object even if
   * the previous URL was hand-edited, and drops the keys a patch sets to
   * `undefined` — which is how clearing the isolate control removes `?isolate=`
   * rather than leaving `?isolate=` behind.
   */
  const onSearchChange = (patch: Partial<ClimateLoopSearch>) =>
    void navigate({
      // Spread into a fresh literal: the router's reducer wants a type with an
      // implicit index signature, which an interface does not have.
      search: (previous: Record<string, unknown>) => ({
        ...parseClimateLoopSearch({ ...previous, ...patch }),
      }),
      replace: true,
    });

  return <ClimateLoopExperiment search={search} onSearchChange={onSearchChange} query={query} />;
}
